import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const API = 'https://api.cpms-srlkevcharge.com';

const PALETTE = ['#1f9a4c', '#2a8fd4', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const POWER_COLORS = { AC: '#2a8fd4', DC: '#f59e0b', Unknown: '#94a3b8' };

const IconChartBar = () => (
  <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

function DashEmptyChart({ message, height = 200 }) {
  return (
    <div className="dash-empty-chart" style={{ height }}>
      <IconChartBar />
      <p>{message}</p>
    </div>
  );
}

export default function EnergyInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/energy`, { params: { days: range } });
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch energy insights', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [range]);

  if (loading) return <div className="loading">Loading energy insights...</div>;
  if (!data) return <div className="loading">No data available</div>;

  const { metrics, energy_trend, energy_by_station, energy_by_tariff, energy_by_hour, energy_by_power_type, energy_by_connector_type } = data;

  const hasEnergy = metrics.total_energy_kwh > 0;
  const powerTypeChartData = Object.entries(energy_by_power_type).map(([name, value]) => ({ name, value }));
  const tariffChartData = energy_by_tariff.filter(t => t.energy_kwh > 0);

  return (
    <div className="dash-page">
      <div className="page-header">
        <h1>Energy Insights</h1>
        <p>A closer look at energy delivery patterns across the network</p>
      </div>

      {/* ── Hero stats + trend, grouped together ── */}
      <div className="dash-panel" style={{ marginBottom: 16 }}>
        <div className="dash-hero-stats" style={{ marginBottom: 20 }}>
          <div className="dash-stat-card hero">
            <div className="dash-stat-label">Total Energy ({range}d)</div>
            <div className="dash-stat-value">{metrics.total_energy_kwh}<span className="unit">kWh</span></div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Sessions with Energy Data</div>
            <div className="dash-stat-value">{metrics.session_count}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Avg Energy per Session</div>
            <div className="dash-stat-value">{metrics.avg_energy_per_session}<span className="unit">kWh</span></div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Peak Usage Hour</div>
            <div className="dash-stat-value" style={{ fontSize: 24 }}>{metrics.peak_hour || '—'}</div>
          </div>
        </div>

        <div className="dash-panel-header">
          <span className="dash-panel-title">Energy Delivered Over Time</span>
          <div className="dash-range-pills">
            {[7, 30, 90].map(d => (
              <button key={d} className={`dash-range-pill ${range === d ? 'active' : ''}`} onClick={() => setRange(d)}>
                {d} days
              </button>
            ))}
          </div>
        </div>

        {!hasEnergy ? (
          <DashEmptyChart message="No energy data yet — this chart will populate as charging sessions complete." height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={energy_trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="energyTrendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1f9a4c" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#1f9a4c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={Math.floor(energy_trend.length / 8)} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, 'Energy']} />
              <Area type="monotone" dataKey="energy_kwh" stroke="#1f9a4c" strokeWidth={2.5} fill="url(#energyTrendGradient)" name="Energy (kWh)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Row 1: Station ranking + Peak hours ── */}
      <div className="dash-grid-2" style={{ marginBottom: 16 }}>
        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Top Stations by Energy Delivered</span></div>
          {energy_by_station.length === 0 ? (
            <DashEmptyChart message="No station energy data yet." />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, energy_by_station.length * 36)}>
              <BarChart data={energy_by_station} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11.5, fill: '#475569' }} width={130} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, 'Energy']} />
                <Bar dataKey="energy_kwh" fill="#1f9a4c" radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Peak Usage Hours</span></div>
          {!hasEnergy ? (
            <DashEmptyChart message="No hourly usage data yet." />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={energy_by_hour} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#94a3b8' }} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, 'Energy']} />
                <Bar dataKey="energy_kwh" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Row 2: Tariff split, Power type split, Connector type ranking ── */}
      <div className="dash-grid-3">
        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Energy by Tariff</span></div>
          {tariffChartData.length === 0 ? (
            <DashEmptyChart message="No tariff-attributed energy yet." />
          ) : (
            <div className="dash-donut-wrap">
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={tariffChartData} cx="50%" cy="50%" innerRadius={42} outerRadius={62} paddingAngle={tariffChartData.length > 1 ? 3 : 0} dataKey="energy_kwh">
                    {tariffChartData.map((entry, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="dash-donut-legend">
                {tariffChartData.map((t, i) => (
                  <div className="dash-donut-legend-row" key={t.name}>
                    <span className="dash-donut-legend-left">
                      <span className="dash-donut-legend-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
                      {t.name}
                    </span>
                    <span className="dash-donut-legend-value">{t.energy_kwh} kWh</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">AC vs DC Energy</span></div>
          {powerTypeChartData.length === 0 ? (
            <DashEmptyChart message="No power type data yet." />
          ) : (
            <div className="dash-donut-wrap">
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={powerTypeChartData} cx="50%" cy="50%" innerRadius={42} outerRadius={62} paddingAngle={powerTypeChartData.length > 1 ? 3 : 0} dataKey="value">
                    {powerTypeChartData.map((entry, i) => (
                      <Cell key={i} fill={POWER_COLORS[entry.name] || PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="dash-donut-legend">
                {powerTypeChartData.map((p, i) => (
                  <div className="dash-donut-legend-row" key={p.name}>
                    <span className="dash-donut-legend-left">
                      <span className="dash-donut-legend-dot" style={{ background: POWER_COLORS[p.name] || PALETTE[i % PALETTE.length] }} />
                      {p.name}
                    </span>
                    <span className="dash-donut-legend-value">{p.value} kWh</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Energy by Connector Type</span></div>
          {energy_by_connector_type.length === 0 ? (
            <DashEmptyChart message="No connector energy data yet." />
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={energy_by_connector_type} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11.5, fill: '#475569' }} width={80} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} kWh`, 'Energy']} />
                <Bar dataKey="energy_kwh" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}